import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  SupportApi,
  extractCaseData,
  extractCaseListData,
  extractThreadListData,
  extractMessageData,
  extractMessageListData,
} from './supportApi';
import {
  SupportCase,
  CaseThread,
  CaseMessage,
  CreateSupportCaseRequest,
  CreateMessageRequest,
  SupportCaseFilters,
} from '../../types/support.types';

// --- Query key factory ---

export const customerSupportKeys = {
  all: ['customerSupport'] as const,
  cases: () => [...customerSupportKeys.all, 'cases'] as const,
  caseList: (accountId: string, filters?: SupportCaseFilters) =>
    [...customerSupportKeys.cases(), 'list', accountId, filters] as const,
  caseDetail: (accountId: string, caseId: string) =>
    [...customerSupportKeys.all, 'caseDetail', accountId, caseId] as const,
  threads: (accountId: string, caseId: string) =>
    [...customerSupportKeys.all, 'threads', accountId, caseId] as const,
  messages: (accountId: string, caseId: string, threadId: string) =>
    [
      ...customerSupportKeys.all,
      'messages',
      accountId,
      caseId,
      threadId,
    ] as const,
};

// --- Query hooks ---

export function useSupportCases(
  accountId?: string,
  filters?: SupportCaseFilters,
) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];

  return useQuery<SupportCase[]>({
    queryKey: customerSupportKeys.caseList(finalAccountId!, filters),
    queryFn: async () => {
      const response = await SupportApi.listCases(
        finalAccountId!,
        filters,
      ).run();
      return extractCaseListData(response);
    },
    enabled: !!finalAccountId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useSupportCase(caseId: string, accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];

  return useQuery<SupportCase>({
    queryKey: customerSupportKeys.caseDetail(finalAccountId!, caseId),
    queryFn: async () => {
      const response = await SupportApi.getCase(finalAccountId!, caseId).run();
      return extractCaseData(response);
    },
    enabled: !!finalAccountId && !!caseId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useSupportCaseThreads(caseId: string, accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];

  return useQuery<CaseThread[]>({
    queryKey: customerSupportKeys.threads(finalAccountId!, caseId),
    queryFn: async () => {
      const response = await SupportApi.listThreads(
        finalAccountId!,
        caseId,
      ).run();
      return extractThreadListData(response);
    },
    enabled: !!finalAccountId && !!caseId,
    refetchInterval: 1000 * 30, // 30 seconds
  });
}

export function useSupportCaseMessages(
  caseId: string,
  threadId: string,
  accountId?: string,
  opts?: { polling?: boolean },
) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];

  return useQuery<CaseMessage[]>({
    queryKey: customerSupportKeys.messages(finalAccountId!, caseId, threadId),
    queryFn: async () => {
      const response = await SupportApi.listMessages(
        finalAccountId!,
        caseId,
        threadId,
      ).run();
      return extractMessageListData(response);
    },
    enabled: !!finalAccountId && !!caseId && !!threadId,
    refetchInterval: opts?.polling ? 1000 * 10 : false, // 10 seconds when active
  });
}

// --- Mutation hooks ---

export function useCreateSupportCase(accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSupportCaseRequest) => {
      const response = await SupportApi.createCase(finalAccountId!, data).run();
      return extractCaseData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customerSupportKeys.cases(),
      });
    },
  });
}

export function useCreateCaseMessage(
  caseId: string,
  threadId: string,
  accountId?: string,
) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMessageRequest) => {
      const response = await SupportApi.createMessage(
        finalAccountId!,
        caseId,
        threadId,
        data,
      ).run();
      return extractMessageData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customerSupportKeys.messages(
          finalAccountId!,
          caseId,
          threadId,
        ),
      });
    },
  });
}

export function useUploadAttachment(
  caseId: string,
  threadId: string,
  accountId?: string,
) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
      file,
    }: {
      messageId: string;
      file: File;
    }) => {
      const result = await SupportApi.uploadAttachment(
        finalAccountId!,
        caseId,
        messageId,
        {
          file_name: file.name,
          content_type: file.type || undefined,
          size_bytes: file.size,
        },
      );
      const uploadUrl = result.data.attributes.upload_url;

      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customerSupportKeys.messages(
          finalAccountId!,
          caseId,
          threadId,
        ),
      });
    },
  });
}
